// src/auth/AuthContext.js
//
// Central place for "is someone logged in, and who" for the whole app.
// Same localStorage keys as before ("token", "userId", "userRole").
//
// Usage:
//   const { isAuthenticated, user, login, logout } = useAuth();

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";

const AuthContext = createContext(null);

const TOKEN_KEY = "token";
const USER_ID_KEY = "userId";
const USER_ROLE_KEY = "userRole";

function readUserFromStorage() {
  const id = localStorage.getItem(USER_ID_KEY);
  const role = localStorage.getItem(USER_ROLE_KEY);
  if (!id && !role) return null;
  return { id, role };
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(readUserFromStorage);

  /**
   * LoginPage se API success ke baad call karo:
   *   login(access_token, { id: user.id, role: user.user_type, name })
   */
  const login = useCallback((newToken, userData) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    if (userData?.id) localStorage.setItem(USER_ID_KEY, userData.id);
    if (userData?.role) localStorage.setItem(USER_ROLE_KEY, userData.role);
    setToken(newToken);
    setUser(userData || null);
  }, []);

  /**
   * Session clear: localStorage + React state.
   */
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(USER_ROLE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    token,
    user, // { id, role, name? } — role: 'admin' | 'care_coordinator' | 'super_admin'
    isAuthenticated: !!token,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth() must be used inside an <AuthProvider>.");
  }
  return ctx;
}